import { useEffect, useState } from "react";
import { X, Mail, CheckCircle2 } from "lucide-react";
import { api } from "@/lib/api";
import { MDX } from "./mdx/tokens";

/**
 * ExitIntentModal — Fires exactly once per session when the visitor's cursor
 * leaves the viewport from the top edge (desktop-only behavior; mobile
 * ignores mouseleave). Captures an email into `/api/waitlist/signup`.
 */
export default function ExitIntentModal({ isDark }) {
    const [open, setOpen] = useState(false);
    const [email, setEmail] = useState("");
    const [status, setStatus] = useState("idle"); // idle · submitting · done · error
    const [msg, setMsg] = useState("");

    useEffect(() => {
        if (typeof window === "undefined") return;
        if (sessionStorage.getItem("mdx.exit.shown") === "1") return;
        // Skip on touch devices — mouseleave is unreliable there.
        if (window.matchMedia?.("(pointer: coarse)").matches) return;

        const handler = (e) => {
            if (e.clientY <= 0 && !sessionStorage.getItem("mdx.exit.shown")) {
                sessionStorage.setItem("mdx.exit.shown", "1");
                setOpen(true);
            }
        };
        // Delay attachment 12s so the visitor sees something first.
        const t = setTimeout(() => document.addEventListener("mouseleave", handler), 12000);
        return () => {
            clearTimeout(t);
            document.removeEventListener("mouseleave", handler);
        };
    }, []);

    const submit = async (e) => {
        e.preventDefault();
        if (!email || !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) {
            setStatus("error");
            setMsg("Please enter a valid email.");
            return;
        }
        setStatus("submitting");
        try {
            await api.post("/waitlist/signup", { email, source: "exit_intent" });
            setStatus("done");
        } catch (err) {
            setStatus("error");
            setMsg(err?.response?.data?.detail || "Something went wrong. Try again in a moment.");
        }
    };

    if (!open) return null;

    const bg = isDark ? "bg-[#0F1524]" : "bg-white";
    const brd = isDark ? "border-[#1F2937]" : "border-[#E5E7EB]";
    const text = isDark ? "text-[#F9FAFB]" : "text-[#090D16]";
    const dim = isDark ? "text-[#9CA3AF]" : "text-[#6B7280]";

    return (
        <div
            className="fixed inset-0 z-[105] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
            onClick={() => setOpen(false)}
            data-testid="exit-intent-modal"
        >
            <div
                onClick={(e) => e.stopPropagation()}
                className={`w-full max-w-md ${bg} ${text} border ${brd} rounded-2xl p-6 shadow-2xl`}
            >
                <div className="flex items-start justify-between mb-3">
                    <div>
                        <div className="font-inter text-[11px] font-bold uppercase tracking-widest mb-1" style={{ color: MDX.greenCta }}>
                            Save your spot
                        </div>
                        <div className="font-inter font-bold text-xl leading-tight">Not ready yet? No pressure.</div>
                    </div>
                    <button onClick={() => setOpen(false)} aria-label="Close" data-testid="exit-intent-close" className={`p-2 rounded-md border ${brd}`}>
                        <X className="h-4 w-4" />
                    </button>
                </div>

                {status === "done" ? (
                    <div className="py-6 text-center" data-testid="exit-intent-success">
                        <CheckCircle2 className="mx-auto h-10 w-10 mb-3" style={{ color: MDX.greenCta }} />
                        <div className="font-inter font-bold text-lg">You're on the list.</div>
                        <p className={`font-roboto text-[14px] mt-1 ${dim}`}>
                            We'll email you the Obondo trial link when you're ready. Karibu.
                        </p>
                    </div>
                ) : (
                    <>
                        <p className={`font-roboto text-[15px] ${dim} mb-4`}>
                            Drop your email — we'll send you the Obondo trial link so you can pick up where you left off.
                        </p>
                        <form onSubmit={submit} className="space-y-3" data-testid="exit-intent-form">
                            <div className={`flex items-center gap-2 rounded-md border ${brd} px-3 py-2.5`}>
                                <Mail className="h-4 w-4 shrink-0" style={{ color: MDX.grayMid }} />
                                <input
                                    type="email"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    placeholder="you@company.com"
                                    data-testid="exit-intent-email"
                                    className={`flex-1 bg-transparent outline-none font-roboto text-[15px] ${text}`}
                                    autoFocus
                                />
                            </div>
                            {status === "error" && (
                                <div className="text-[13px] font-roboto" style={{ color: MDX.orangeHi }}>{msg}</div>
                            )}
                            <button
                                type="submit"
                                disabled={status === "submitting"}
                                data-testid="exit-intent-submit"
                                style={{ background: MDX.greenCtaSolid }}
                                className="w-full font-inter font-bold uppercase tracking-wider text-white rounded-md py-3 text-[15px] hover:opacity-90 transition-opacity disabled:opacity-60"
                            >
                                {status === "submitting" ? "Saving…" : "Save My Spot"}
                            </button>
                        </form>
                        <p className={`font-roboto text-[11px] mt-3 ${dim}`}>
                            One email. No newsletters. Unsubscribe with a single click.
                        </p>
                    </>
                )}
            </div>
        </div>
    );
}
